import { Injectable, NgZone, OnDestroy } from '@angular/core'
import { BehaviorSubject, Observable, interval } from 'rxjs'
import { map, takeWhile } from 'rxjs/operators'
import { CareFinitySession } from './CareFinitySession'
import { AppSettingService } from '../../api/services'

@Injectable()
export class SessionTimeOutService implements OnDestroy {
  private runTimer: boolean = false
  private runSecondTimer: boolean = false
  private maxIdleTimeAllowed: number = 2
  private showWarningBefore: number = 1

  public timeOut: BehaviorSubject<SessionTimeOutEventArgs>
  public countDown: BehaviorSubject<SessionTimeOutEventArgs>

  private sessionForIdle!: Observable<number>
  private userActivityChangeCallback!: (event: any) => void

  public clockForIdle!: Observable<number>

  constructor(
    private zone: NgZone,
    private appSettingService: AppSettingService,
  ) {
    this.timeOut = new BehaviorSubject<SessionTimeOutEventArgs>(
      this.buildEvent(SessionTimeOutEventType.InitiateTimer, ''),
    )
    this.countDown = new BehaviorSubject<SessionTimeOutEventArgs>(
      this.buildEvent(SessionTimeOutEventType.InitiateTimer, ''),
    )
    this.maxIdleTimeAllowed =
      CareFinitySession.applicationSettings.accountPolicy.sessionTimeoutMinutes
    this.showWarningBefore =
      CareFinitySession.applicationSettings.accountPolicy
        .sessionExpiryWarningBeforeSeconds / 60
  }

  public initilizeSessionTimeout(): void {
    this.runTimer = true

    if (this.maxIdleTimeAllowed === 0) {
      this.timeOut.error('Please provide maxIdleTimeAllowed in minuite')
      return
    }

    this.reset()
    this.initListener()
    this.initInterval()
  }

  private initListener(): void {
    this.zone.runOutsideAngular(() => {
      this.userActivityChangeCallback = ($event) =>
        this.handleUserActiveState($event)
      window.document.addEventListener(
        'click',
        this.userActivityChangeCallback.bind(this),
        true,
      )
      window.document.addEventListener(
        'mousemove',
        this.userActivityChangeCallback.bind(this),
        true,
      )
      window.document.addEventListener(
        'scroll',
        this.userActivityChangeCallback.bind(this),
        true,
      )
      window.document.addEventListener(
        'keydown',
        this.userActivityChangeCallback.bind(this),
        true,
      )
    })
  }

  handleUserActiveState(event: any): void {
    this.reset()
  }

  public reset(): void {
    CareFinitySession.lastUserActivity = Date.now()

    if (this.timeOut) {
      this.timeOut.next(this.buildEvent(SessionTimeOutEventType.ResetTimer, ''))
    }
  }

  public stopIdleTimer(): void {
    this.runTimer = false
  }

  public stopWarningTimer(): void {
    this.runSecondTimer = false
  }

  private initInterval(): void {
    this.zone.runOutsideAngular(() => {
      const intervalDuration = 1000
      this.sessionForIdle = interval(intervalDuration).pipe(
        map((tick: number) => {
          return tick
        }),
        takeWhile(() => this.runTimer),
      )

      this.check()
    })
  }

  private alreadyExpired(): boolean {
    const now = Date.now()
    const max =
      CareFinitySession.lastUserActivity + this.maxIdleTimeAllowed * 60 * 1000
    let diff = max - now

    if (diff <= 0) {
      this.zone.run(() => {
        this.countDown.next(
          this.buildEvent(SessionTimeOutEventType.TimerCompleted, ''),
        )
      })
      return true
    }

    return false
  }

  private check(): void {
    this.sessionForIdle.subscribe(() => {
      if (this.alreadyExpired()) {
        return
      }

      const now = Date.now()
      const timeleft =
        CareFinitySession.lastUserActivity +
        (this.maxIdleTimeAllowed - this.showWarningBefore) * 60 * 1000
      const diff = timeleft - now

      if (diff < 0) {
        window.document.removeEventListener(
          'click',
          this.userActivityChangeCallback,
          true,
        )
        window.document.removeEventListener(
          'mousemove',
          this.userActivityChangeCallback,
          true,
        )
        window.document.removeEventListener(
          'scroll',
          this.userActivityChangeCallback,
          true,
        )
        window.document.removeEventListener(
          'keydown',
          this.userActivityChangeCallback,
          true,
        )
        this.zone.run(() => {
          if (this.timeOut) {
            this.timeOut.next(
              this.buildEvent(SessionTimeOutEventType.TimerCompleted, ''),
            )

            if (this.showWarningBefore > 0) {
              this.countDown.next(
                this.buildEvent(SessionTimeOutEventType.TimerStarted, ''),
              )
              this.executeFinalTimer()
            }
          }
          this.runTimer = false
        })
      } else {
        if (this.timeOut)
          this.timeOut.next(
            this.buildEvent(SessionTimeOutEventType.TimerTick, ''),
          )
      }
    })
  }

  private executeFinalTimer = () => {
    this.runSecondTimer = true
    this.initializeFinalTimer()
  }

  private initializeFinalTimer(): void {
    const intervalDuration = 1000
    this.clockForIdle = interval(intervalDuration).pipe(
      map((tick: number) => {
        return tick
      }),
      takeWhile(() => this.runSecondTimer),
    )

    this.checkUserActionTime()
  }

  private checkUserActionTime(): void {
    this.alreadyExpired()
    let timeInSecond = this.showWarningBefore * 60
    let timeInMin = this.showWarningBefore - 1
    this.clockForIdle.subscribe(() => {
      this.alreadyExpired()
      if (--timeInSecond === 0) {
        if (--timeInMin === 0) {
          timeInMin = timeInMin > 0 ? timeInMin - 1 : 0
        }
        if (timeInMin === -1 && timeInSecond === 0) {
          this.runSecondTimer = false

          if (this.countDown) {
            this.countDown.next(
              this.buildEvent(SessionTimeOutEventType.TimerCompleted, ''),
            )
          }
        }
        if (timeInMin < 0) {
          timeInMin = 0
          setTimeout(() => {
            timeInSecond = this.showWarningBefore
          }, 800)
        } else {
          timeInSecond = this.showWarningBefore
        }
      }

      this.countDown.next(
        this.buildEvent(
          SessionTimeOutEventType.TimerTick,
          `${timeInMin < 0 ? '00' : timeInMin}:${timeInSecond}`,
        ),
      )
    })
  }

  ngOnDestroy(): void {}

  buildEvent(
    type: SessionTimeOutEventType,
    args: string,
  ): SessionTimeOutEventArgs {
    let e = new SessionTimeOutEventArgs()

    e.eventType = type
    e.args = args
    return e
  }
}

export enum SessionTimeOutEventType {
  Unknown,
  ResetTimer,
  InitiateTimer,
  TimerStarted,
  TimerStopped,
  TimerTick,
  TimerCompleted,
}

export class SessionTimeOutEventArgs {
  eventType: SessionTimeOutEventType = SessionTimeOutEventType.Unknown
  args: string = ''
}
